import { useParams, Link } from "react-router-dom";
import { FadeUp, MaskedLineInView, Page } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import BlogCard from "@/components/BlogCard";
import SiteEnd from "@/components/SiteEnd";
import blogs, { getBlogBySlug } from "@/data/blogs";
import { insightsCopy } from "@/data/site";

export default function BlogDetail() {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);

  if (!post) {
    return (
      <Page testId="blog-not-found-page">
        <PageHero title="Blogs" testId="blog-detail-heading" />
        <div className="relative z-10 site-container pb-20 text-center">
          <p className="text-xl text-[#111]/60">We couldn't find that article.</p>
          <Link to="/blogs" className="btn-pill btn-lav mt-6 inline-flex !py-3 !px-6 text-[15px] !font-bold">
            Back to Blogs
          </Link>
        </div>
        <SiteEnd />
      </Page>
    );
  }

  const others = blogs.filter((b) => b.slug !== post.slug).slice(0, 2);

  return (
    <Page testId="blog-detail-page">
      <PageHero title="Blogs" testId="blog-detail-heading" />

      <section className="relative z-10 site-container">
        <article
          data-testid="blog-article"
          className="glass-surface relative rounded-[32px] px-6 sm:px-12 lg:px-[92px] py-12 sm:py-24 overflow-hidden"
        >
          <div className="relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <FadeUp className="lg:col-span-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#111] flex items-center justify-center">
                  <span className="text-white font-extrabold text-sm">O</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-[#111]/55">{post.author.name}</p>
                  <p data-testid="blog-author" className="text-[15px] font-bold">{post.author.role}</p>
                </div>
              </div>
            </FadeUp>

            <div className="lg:col-span-9">
              <FadeUp>
                <span data-testid="blog-date" className="inline-block rounded-full border border-[#111]/20 bg-white px-5 py-2 text-sm font-semibold shadow-sm">
                  {post.dateLabel}
                </span>
              </FadeUp>
              <h1 data-testid="blog-title" className="mt-6 text-3xl sm:text-4xl lg:text-[40px] font-semibold tracking-tight leading-tight max-w-3xl">
                <MaskedLineInView>{post.title}</MaskedLineInView>
              </h1>
            </div>
          </div>

          <FadeUp className="mt-10">
            <div className="rounded-2xl overflow-hidden max-h-[560px]">
              <img data-testid="blog-hero-image" src={post.image} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </FadeUp>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="hidden lg:block lg:col-span-3" />
            <div className="lg:col-span-8" data-testid="blog-content">
              <FadeUp>
                <p className="text-base sm:text-lg leading-relaxed text-[#111]/65">{post.intro}</p>
              </FadeUp>
              {post.sections.map((s, i) => (
                <FadeUp key={i} delay={0.05} className="mt-10">
                  <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">{s.heading}</h2>
                  {s.paragraphs.map((p, j) => (
                    <p key={j} className="mt-4 text-base leading-relaxed text-[#111]/65">{p}</p>
                  ))}
                  {s.bullets.length > 0 && (
                    <ul className="mt-4 space-y-2.5 list-disc pl-5 text-base leading-relaxed text-[#111]/65 marker:text-[#8C5EBA]">
                      {s.bullets.map((b, k) => (
                        <li key={k}>{b}</li>
                      ))}
                    </ul>
                  )}
                </FadeUp>
              ))}
            </div>
          </div>
          </div>
        </article>
      </section>

      <section data-testid="explore-more-section" className="relative z-10 site-container py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.12]">
              <MaskedLineInView>Explore More</MaskedLineInView>
              <MaskedLineInView delay={0.1}>
                <span>Of <span className="text-[#8C5EBA]">Our Articles</span></span>
              </MaskedLineInView>
            </h2>
          </div>
          <FadeUp className="lg:col-span-6" delay={0.15}>
            <p className="text-base leading-relaxed text-[#111]/60 max-w-md lg:ml-auto">{insightsCopy.desc}</p>
          </FadeUp>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {others.map((p, i) => (
            <BlogCard key={p.slug} post={p} index={i} />
          ))}
        </div>
      </section>

      <SiteEnd />
    </Page>
  );
}
